import { useApi, useGetParameters } from '@composables/useApi';
import { defineStore } from 'pinia';
import {
	FailedCreatePaymentInvoiceError,
	FailedGetPaymentProvidersError,
	FailedGetPaymentTariffsError,
	FailedCheckPromoCodeError,
	FailedApplyPromoCodeError,
	Bill,
	PaymentInvoice,
	PaymentProvider,
	PaymentTariff,
	PromoCode
} from "@store/api/interfaces";
import { useAuthApi } from "@store/api/authApi";

// GA4 context + diagnostics captured on the checkout page at invoice creation.
export interface InvoiceOptions {
	email?: string;
	promoCode?: string;
	gaClientId?: string;
	gaSessionId?: string;
	gaStatus?: string;
	gaSessionAgeSec?: number;
	gtmLoaded?: boolean;
	capturedAt?: string;
	source?: string;
	pageLocation?: string;
}

export const usePaymentApi = defineStore('paymentApi', () => {
	const authApi = useAuthApi();

	// Auth header is optional for public payment endpoints
	const getOptionalAuthHeaders = (): HeadersInit => {
		const token = authApi.methods.getToken();
		if (!token) return {};
		return { 'Authorization': `Bearer ${token}` };
	};

	const http = {
		getProviders: async (): Promise<PaymentProvider[]> => {
			try {
				return await useApi<PaymentProvider[]>('/payment/provider', {
					method: "GET",
					headers: getOptionalAuthHeaders()
				});
			} catch (error: any) {
				console.error('failed get providers', error);
				throw new FailedGetPaymentProvidersError(error.message);
			}
		},

		getTariffs: async (promoCode?: string): Promise<PaymentTariff[]> => {
			try {
				let path = '/payment/tariff';
				if (promoCode) {
					path = useGetParameters(path, { promo_code: promoCode });
				}
				return await useApi<PaymentTariff[]>(path, {
					method: "GET",
					headers: getOptionalAuthHeaders()
				});
			} catch (error: any) {
				console.error('failed get tariffs', error);
				throw new FailedGetPaymentTariffsError(error.message);
			}
		},

		invoice: async (tariffId: number, providerCode: string, data: object, options?: InvoiceOptions): Promise<PaymentInvoice> => {
			try {
				const body: Record<string, any> = { tariff: tariffId, provider: providerCode, data };
				if (options?.email) {
					body.email = options.email.toLowerCase();
				}
				if (options?.promoCode) {
					body.promo_code = options.promoCode;
				}
				// Stored in bill.data.ga so the bot can send the server-side
				// GA4 purchase with the buyer's client_id.
				if (options?.gaClientId) {
					body.ga_client_id = options.gaClientId;
				}
				if (options?.gaSessionId) {
					body.ga_session_id = options.gaSessionId;
				}
				// Diagnostics: why an identifier above is missing. Half of the
				// purchases lose their ad source and the reason is invisible
				// after the fact — these fields split that by cause and make the
				// affected bills findable one by one. Sent even when the fields
				// are unknown to the backend: extra keys are ignored there.
				if (options?.gaStatus) {
					body.ga_status = options.gaStatus;
				}
				if (options?.gaSessionAgeSec !== undefined) {
					body.session_age_sec = options.gaSessionAgeSec;
				}
				if (options?.gtmLoaded !== undefined) {
					body.gtm_loaded = options.gtmLoaded;
				}
				if (options?.capturedAt) {
					body.captured_at = options.capturedAt;
				}
				if (options?.source) {
					body.source = options.source;
				}
				if (options?.pageLocation) {
					body.page_location = options.pageLocation;
				}
				return await useApi<PaymentInvoice>('/payment/invoice', {
					method: "POST",
					headers: getOptionalAuthHeaders(),
					body: JSON.stringify(body)
				});
			} catch (error: any) {
				console.error('failed create invoice', error);
				throw new FailedCreatePaymentInvoiceError(error.message, error.code);
			}
		},

		// Works without auth — access is gated by the HMAC status_token from the
		// invoice response. Returns null on any error so the caller keeps polling.
		getInvoiceStatus: async (billId: number, statusToken: string): Promise<Bill|null> => {
			try {
				return await useApi<Bill>(useGetParameters(`/payment/invoice/${billId}/status`, { token: statusToken }), {
					method: "GET"
				});
			} catch (error: any) {
				console.info('failed get invoice status', error);
				return null;
			}
		},

		// Works without auth (rate limited: 10/hour per IP)
		checkPromoCode: async (code: string): Promise<{ exists: boolean }> => {
			try {
				return await useApi<{ exists: boolean }>('/payment/promo/check', {
					method: "POST",
					headers: getOptionalAuthHeaders(),
					body: JSON.stringify({ code })
				});
			} catch (error: any) {
				console.error('failed check promo code', error);
				throw new FailedCheckPromoCodeError(error.message);
			}
		},

		// Requires auth
		cancelPromoCode: async (code: string): Promise<void> => {
			try {
				await useApi('/payment/promo/cancel', {
					method: "POST",
					headers: authApi.methods.getAuthHeaders(),
					body: JSON.stringify({ code })
				});
			} catch (error: any) {
				console.error('failed cancel promo code', error);
				throw error;
			}
		},

		// Requires auth
		applyPromoCode: async (code: string): Promise<PromoCode> => {
			try {
				return await useApi<PromoCode>('/payment/promo/apply', {
					method: "POST",
					headers: authApi.methods.getAuthHeaders(),
					body: JSON.stringify({ code })
				});
			} catch (error: any) {
				console.error('failed apply promo code', error);
				throw new FailedApplyPromoCodeError(error.message, error.code);
			}
		}
	};

	// Codes the backend rejects a promo with for this user. Retrying the same code
	// gets the same answer, so it must be forgotten rather than carried to the
	// checkout: tariffs by ?promo_code= don't check it's usable and would show a
	// discounted price the invoice then refuses.
	const PROMO_REJECTION_CODES = ['promo_code_not_found', 'promo_code_expired', 'promo_code_own', 'promo_code_already_used'];

	const methods = {
		// The rejection code when the backend refused the promo; undefined for
		// network, rate-limit and other failures — they say nothing about the code.
		promoRejectionCode: (error: unknown): string | undefined => {
			const code = error instanceof FailedApplyPromoCodeError || error instanceof FailedCreatePaymentInvoiceError
				? error.code
				: undefined;
			return code && PROMO_REJECTION_CODES.includes(code) ? code : undefined;
		}
	};

	return {
		http,
		methods
	};
});
